import React from "react";
import { FiEye } from "react-icons/fi";
import { FormLabel, Switch } from "@chakra-ui/react";

import { useIsReadOnlyMode } from "newui/features/ExecutiveView";
import { useRfxData } from "newui/features/Rfx/data-provider/RfxDataProvider";

/**
 * Dedicated top-of-RFx banner that lets a switched executive (read-only) toggle
 * between the normal read-only view and a read-only preview of the RFx Lead's
 * management UI.
 *
 * WRITE-SAFETY: this only flips `viewAsRfxLead` in RfxDataProvider, which feeds
 * `effectiveIsRfxLead` for VISIBILITY gating ONLY. It never feeds any
 * readOnly/canEdit/canManage computation, and the existing pointer-events-none
 * read-only wrapper stays in place, so switching to the Lead view can never
 * enable a write.
 *
 * Only rendered for a switched executive (useIsReadOnlyMode) on a loaded RFx.
 * Normal users and genuine RFx leads see nothing.
 */
export const RfxLeadViewToggleBanner = () => {
	const isReadOnly = useIsReadOnlyMode();
	const rfxContext = useRfxData(true);
	const rfxData = rfxContext?.rfxData;
	const viewAsRfxLead = rfxContext?.viewAsRfxLead ?? false;
	const setViewAsRfxLead = rfxContext?.setViewAsRfxLead;

	// Gate: switched executive only, and only once an RFx is in context.
	if (!isReadOnly || !rfxData || !setViewAsRfxLead) {
		return null;
	}

	const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
		setViewAsRfxLead(event.target.checked);
	};

	return (
		<div
			role="status"
			aria-live="polite"
			data-testid="rfx-lead-view-toggle-banner"
			className="flex min-h-12 w-full flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900"
		>
			<div className="flex items-center gap-2">
				<FiEye aria-hidden="true" />
				<span>
					<span className="font-semibold">Read-only preview.</span>{" "}
					{viewAsRfxLead
						? "You are previewing the RFx Lead's management view. Editing stays disabled."
						: "Switch on to preview the RFx Lead's management view (read-only)."}
				</span>
			</div>
			<div className="flex items-center gap-2">
				<FormLabel htmlFor="rfx-lead-view-toggle-switch" mb="0" className="font-medium">
					{viewAsRfxLead ? "Viewing as RFx Lead" : "Viewing as normal user"}
				</FormLabel>
				<Switch
					id="rfx-lead-view-toggle-switch"
					isChecked={viewAsRfxLead}
					onChange={handleToggle}
					colorScheme="blue"
					aria-label="Toggle viewing the RFx as the RFx Lead"
					data-testid="rfx-lead-view-toggle-switch"
				/>
			</div>
		</div>
	);
};
